import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { renderInline } from "./RichText";

/**
 * Reusable FAQ accordion. Pass `faqs` as [{ q, a }] or [{ question, answer }].
 * Visual text is rendered from the exact same data used for the FAQPage JSON-LD
 * (see buildFaqSchema in JsonLd.jsx), so schema and visible content always match.
 */
export const FAQSection = ({ faqs = [], title = "Frequently asked questions" }) => {
  const [open, setOpen] = useState(null);
  const items = (faqs || [])
    .map((f) => ({ q: f.q ?? f.question, a: f.a ?? f.answer }))
    .filter((f) => f.q && f.a);
  if (!items.length) return null;

  return (
    <Reveal>
      <section data-testid="faq-section" className="mt-16 border-t border-border/60 pt-10">
        <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-primary mb-2">
          <HelpCircle size={12} strokeWidth={1.8} className="inline mr-1.5 -mt-0.5" />
          FAQ
        </p>
        <h2 className="font-headings text-2xl sm:text-3xl text-foreground">{title}</h2>
        <div className="mt-6 border border-border/60 divide-y divide-border/50">
          {items.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="bg-secondary/40">
                <button
                  type="button"
                  data-testid={`faq-question-${i}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-5 hover:bg-secondary/70 transition-colors"
                >
                  <span className="font-headings text-lg text-foreground leading-snug">{f.q}</span>
                  <ChevronDown
                    size={17}
                    className={`shrink-0 mt-1 text-primary transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  data-testid={`faq-answer-${i}`}
                  className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 sm:px-6 pb-6 font-body font-light text-foreground/75 leading-relaxed">
                      {renderInline(f.a)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </Reveal>
  );
};
